import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { EntraIdGroupMember } from '../../interfaces/admin/EntraIdGroupMember';
import { GckUserAssignmentModel, GckUserAssignmentService } from '../../services/gck-user-assignment.service';
import { WorkflowOptionSetService } from '../../services/workflow-option-set.service';
import { GraphService } from '../../../auth/services/graph.service';
import { AdminState } from '../admin/admin.state';
import { LoadAllUserGckAssignments, LoadGckOptionsForDate, SaveUserGckAssignments } from './gck-assignment.actions';

export interface UserGckAssignmentData {
    user: EntraIdGroupMember;
    assignedGcks: string[];
}

export interface GckAssignmentStateModel {
    userAssignments: UserGckAssignmentData[];
    availableGcks: string[];
    isLoading: boolean;
    error: string | null;
}

@State<GckAssignmentStateModel>({
    name: 'gckAssignment',
    defaults: {
        userAssignments: [],
        availableGcks: [],
        isLoading: false,
        error: null
    }
})
@Injectable()
export class GckAssignmentState {
    private readonly gckUserAssignmentService = inject(GckUserAssignmentService);
    private readonly workflowOptionSetService = inject(WorkflowOptionSetService);
    private readonly graphService = inject(GraphService);
    private readonly store = inject(Store);

    @Selector()
    static userAssignments(state: GckAssignmentStateModel): UserGckAssignmentData[] {
        return state.userAssignments;
    }

    @Selector()
    static availableGcks(state: GckAssignmentStateModel): string[] {
        return state.availableGcks;
    }

    @Selector()
    static isLoading(state: GckAssignmentStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static error(state: GckAssignmentStateModel): string | null {
        return state.error;
    }

    @Action(LoadAllUserGckAssignments)
    loadAllUserGckAssignments(ctx: StateContext<GckAssignmentStateModel>): Observable<UserGckAssignmentData[]> {
        ctx.patchState({ isLoading: true, error: null });

        return this.loadAllGroupMembers().pipe(
            switchMap(members => {
                if (members.length === 0) {
                    return of([]);
                }
                return forkJoin(members.map(member => this.loadGcksForUser(member)));
            }),
            tap(userAssignments => {
                ctx.patchState({ userAssignments });
            }),
            catchError(error => {
                ctx.patchState({ error: error.message || 'Failed to load user GCK assignments' });
                return of([]);
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(LoadGckOptionsForDate)
    loadGckOptions(ctx: StateContext<GckAssignmentStateModel>, action: LoadGckOptionsForDate): Observable<string[]> {
        ctx.patchState({ isLoading: true, error: null });

        return this.workflowOptionSetService.getWorkflowFieldOptionSet(action.date, 'gck').pipe(
            tap(optionSetGcks => {
                const currentAssignments = ctx.getState().userAssignments;
                const assignedGcks = currentAssignments.flatMap(ua => ua.assignedGcks.map(gck => gck.trim()));
                const mergedGcks = Array.from(new Set([...optionSetGcks, ...assignedGcks]));
                ctx.patchState({ availableGcks: mergedGcks });
            }),
            catchError(error => {
                ctx.patchState({ error: error.message || 'Failed to load GCK options' });
                return of([]);
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    @Action(SaveUserGckAssignments)
    saveUserGckAssignments(
        ctx: StateContext<GckAssignmentStateModel>,
        action: SaveUserGckAssignments
    ): Observable<unknown[]> {
        ctx.patchState({ isLoading: true, error: null });

        const ERROR_MARKER = { _error: true };

        const allChanges$ = action.changes.flatMap(change => [
            ...change.addedGcks.map(gck =>
                this.gckUserAssignmentService
                    .createAssignment(gck, change.userId)
                    .pipe(catchError(() => of(ERROR_MARKER)))
            ),
            ...change.removedGcks.map(gck =>
                this.gckUserAssignmentService
                    .deleteAssignment(gck, change.userId)
                    .pipe(catchError(() => of(ERROR_MARKER)))
            )
        ]);

        if (allChanges$.length === 0) {
            ctx.patchState({ isLoading: false });
            return of([]);
        }

        return forkJoin(allChanges$).pipe(
            tap((results: unknown[]) => {
                const hasErrors = results.some((result: unknown) => result === ERROR_MARKER);
                if (hasErrors) {
                    ctx.patchState({ error: 'Some GCK assignments failed to save' });
                    throw new Error('Some GCK assignments failed to save');
                } else {
                    const currentAssignments = ctx.getState().userAssignments;
                    const updatedAssignments = currentAssignments.map(ua => {
                        const change = action.changes.find(c => c.userId === ua.user.id);
                        if (change) {
                            const newGcks = [
                                ...ua.assignedGcks.filter(g => !change.removedGcks.includes(g)),
                                ...change.addedGcks
                            ];
                            return { ...ua, assignedGcks: newGcks };
                        }
                        return ua;
                    });
                    ctx.patchState({ userAssignments: updatedAssignments });
                }
            }),
            catchError(error => {
                const errorMessage = error.message || 'Failed to save GCK assignments';
                ctx.patchState({ error: errorMessage });
                throw error;
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }

    private loadAllGroupMembers(): Observable<EntraIdGroupMember[]> {
        const entraIdGroups = this.getEntraIdGroupsFromAdminState();
        if (entraIdGroups.length === 0) {
            return of([]);
        }

        return forkJoin(
            entraIdGroups.map(group =>
                this.graphService
                    .getGroupMembers(group.id)
                    .pipe(catchError(() => of({ value: [] as EntraIdGroupMember[] })))
            )
        ).pipe(
            map((responses: { value: EntraIdGroupMember[] }[]) => {
                const allMembers = responses.flatMap(
                    (response: { value: EntraIdGroupMember[] }) => response.value ?? []
                );
                const uniqueMembers = Array.from(
                    new Map(allMembers.map((member: EntraIdGroupMember) => [member.id, member])).values()
                );
                return uniqueMembers;
            })
        );
    }

    private loadGcksForUser(user: EntraIdGroupMember): Observable<UserGckAssignmentData> {
        return this.gckUserAssignmentService.getGcksByUser(user.id).pipe(
            map((assignments: GckUserAssignmentModel[]) => ({
                user,
                assignedGcks: assignments.map(a => a.gckId.trim())
            })),
            catchError(() =>
                of({
                    user,
                    assignedGcks: []
                })
            )
        );
    }

    private getEntraIdGroupsFromAdminState(): EntraIdGroupMember[] {
        return this.store.selectSnapshot(AdminState.entraIdGroupMembers);
    }
}
